/**
 * Company Research Service
 * Handles company research and data management operations
 */
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { createErrorLogger, ErrorType } from '../utils/logger';

const logger = createErrorLogger('CompanyAnalyzerService');

const CompanyAnalyzerService = {
  /**
   * Research a company to gather information
   * @param {string} companyName - The company name to research
   * @param {File[]} files - Optional files for additional context
   * @param {string} userId - Optional user ID for tracking
   * @returns {Promise<{ companyName: string, report: string, sections: Array }>}
   */
  researchCompany: async (companyName, files = [], userId = null) => {
    logger.info('researchCompany', `researchCompany called for company: ${companyName}`, { companyName, userId });
    const formData = new FormData();
    formData.append('company_name', companyName || '');
    files.forEach(file => formData.append('files', file));
    if (userId) {
      formData.append('user_id', userId);
    }

    const response = await axios.post(
      `${API_BASE_URL}/company/research`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    const { result } = response.data;

    // Extract structured data from the response
    let sections = [];
    let report = '';

    if (result) {
      if (typeof result === 'string') {
        report = result;
        // Try to parse sections from the report
        sections = parseReportSections(report);
      } else if (typeof result === 'object') {
        report = result.report || result.content || '';
        sections = result.sections || parseReportSections(report);
      }
    }

    return { companyName, report, sections };
  },

  /**
   * Load company research data directly from disk
   * @param {string} companyName - The company name to load data for
   * @returns {Promise<{ companyName: string, report: string, sections: Array }>}
   */
  loadCompanyData: async (companyName) => {
    logger.info('loadCompanyData', `Loading company data from disk for company: ${companyName}`, {
      companyName,
      timestamp: new Date().toISOString()
    });
    logger.info('loadCompanyData', `API URL: ${API_BASE_URL}/company/load`, {
      url: `${API_BASE_URL}/company/load`,
      fullRequestUrl: `${API_BASE_URL}/company/load?company_name=${encodeURIComponent(companyName)}`
    });

    try {
      logger.info('loadCompanyData', 'Making API request to load company data...', {
        companyName,
        method: 'GET'
      });

      const response = await axios.get(
        `${API_BASE_URL}/company/load`,
        { params: { company_name: companyName } }
      );

      logger.info('loadCompanyData', 'API response received', {
        status: response.status,
        hasResult: !!response.data?.result,
        message: response.data?.message,
        resultType: typeof response.data?.result
      });

      const { result } = response.data;

      if (!result) {
        logger.info('loadCompanyData', 'No company data found on disk, returning empty data structure', {
          companyName,
          returnedStructure: { companyName, report: '', sections: [] }
        });
        return { companyName, report: '', sections: [] };
      }

      // Extract structured data from the response
      let sections = [];
      let report = '';

      if (typeof result === 'string') {
        report = result;
        sections = parseReportSections(report);
      } else if (typeof result === 'object') {
        report = result.report || result.content || '';
        sections = result.sections || parseReportSections(report);
      }

      logger.info('loadCompanyData', 'Successfully processed company data from disk', {
        companyName,
        sectionCount: sections.length,
        reportLength: report.length,
        hasReport: !!report,
        hasSections: sections.length > 0,
        timestamp: new Date().toISOString()
      });
      return { companyName, report, sections };
    } catch (error) {
      logger.error('loadCompanyData', 'Error loading company data from disk', error, ErrorType.API_CALL);
      logger.error('loadCompanyData', 'Error details', {
        companyName,
        message: error.message,
        errorType: error.name,
        timestamp: new Date().toISOString()
      }, ErrorType.API_CALL);
      if (error.response) {
        logger.error('loadCompanyData', 'API error response', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          url: error.response.config?.url
        }, ErrorType.API_CALL);
      }
      throw error;
    }
  },

  /**
   * Get the company name associated with a user
   * @param {string} userId - The user ID to get company mapping for
   * @returns {Promise<{ userId: string, companyName: string }>}
   */
  getUserCompanyMapping: async (userId) => {
    logger.info('getUserCompanyMapping', `Getting company mapping for user: ${userId}`, { userId });

    try {
      const response = await axios.get(`${API_BASE_URL}/training/user/company-mapping/${userId}`);
      const { result } = response.data;

      if (!result || !result.companyName) {
        logger.info('getUserCompanyMapping', 'No company mapping found', { userId });
        return { userId, companyName: null };
      }

      logger.info('getUserCompanyMapping', 'Company mapping found', { userId, companyName: result.companyName });
      return { userId, companyName: result.companyName };
    } catch (error) {
      logger.error('getUserCompanyMapping', 'Error getting company mapping', error, ErrorType.API_CALL);
      return { userId, companyName: null };
    }
  },

  /**
   * Save company research data to disk via API
   * @param {string} companyName - The company name
   * @param {Object} companyData - The company data to save
   * @returns {Promise<Object>} The saved company data
   */
  saveCompanyData: async (companyName, companyData) => {
    logger.info('saveCompanyData', `Saving company data for: ${companyName}`, { companyName });

    try {
      const formData = new FormData();
      formData.append('company_name', companyName);
      formData.append('company_data', JSON.stringify(companyData));

      const response = await axios.post(
        `${API_BASE_URL}/company/save`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      logger.info('saveCompanyData', 'Company data saved successfully', { companyName });
      return response.data;
    } catch (error) {
      logger.error('saveCompanyData', 'Error saving company data', error, ErrorType.API_CALL);
      throw error;
    }
  }
};

/**
 * Helper function to parse sections from a report string
 * @param {string} report - The report text to parse
 * @returns {Array} Array of section objects
 */
function parseReportSections(report) {
  if (!report || typeof report !== 'string') {
    return [];
  }

  const sections = [];
  const lines = report.split('\n');
  let currentSection = null;

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Check if this is a section header (starts with # or ##)
    if (trimmedLine.startsWith('#')) {
      // Save the previous section if it exists
      if (currentSection) {
        sections.push(currentSection);
      }

      // Start a new section
      const title = trimmedLine.replace(/^#+\s*/, '');
      currentSection = {
        title,
        content: ''
      };
    } else if (currentSection && trimmedLine) {
      // Add content to the current section
      currentSection.content += (currentSection.content ? '\n' : '') + trimmedLine;
    }
  }

  // Add the last section if it exists
  if (currentSection) {
    sections.push(currentSection);
  }

  return sections;
}

export default CompanyAnalyzerService;